import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Search, ChevronDown, ChevronUp, X, Check, LayoutGrid, Table2, Download } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { format } from 'date-fns';

// Multi-select dropdown with fuzzy search
function MultiSelectFilter({ label, options, selected, onSelectionChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onSelectionChange(selected.filter(v => v !== value));
    } else {
      onSelectionChange([...selected, value]);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-slate-400 text-xs">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between bg-slate-950 border-slate-700 text-slate-100 h-9 px-3"
          >
            <span className="truncate text-left flex-1">
              {selected.length === 0 ? placeholder : `${selected.length} selected`}
            </span>
            <ChevronDown className="w-4 h-4 ml-2 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput 
              placeholder={`Search ${label.toLowerCase()}...`} 
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    onSelect={() => toggleOption(option.value)}
                    className="cursor-pointer"
                  >
                    <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                      selected.includes(option.value) 
                        ? 'bg-indigo-600 border-indigo-600 text-white' 
                        : 'border-slate-600'
                    }`}>
                      {selected.includes(option.value) && <Check className="h-3 w-3" />}
                    </div>
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function IssueSearch({ 
  issues, 
  onFilteredResults, 
  officers = [], 
  missions = [],
  objectives = [],
  activities = [],
  artifacts = [],
  rituals = [],
  policies = [],
  capabilities = [],
  viewMode = 'card', 
  onViewModeChange 
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [severityFilter, setSeverityFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState([]);
  const [reportedByFilter, setReportedByFilter] = useState([]);
  const [contextFilter, setContextFilter] = useState([]);
  const [officerTagFilter, setOfficerTagFilter] = useState([]);
  const [artifactTagFilter, setArtifactTagFilter] = useState([]);
  const [ritualTagFilter, setRitualTagFilter] = useState([]);
  const [policyTagFilter, setPolicyTagFilter] = useState([]);
  const [capabilityTagFilter, setCapabilityTagFilter] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'decision_made', label: 'Decision Made' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'archived', label: 'Archived' }
  ];

  const categoryOptions = [
    { value: 'red_line_violation', label: 'Red Line Violation' },
    { value: 'execution_headwind', label: 'Execution Headwind' },
    { value: 'capability_gap', label: 'Capability Gap' },
    { value: 'process_friction', label: 'Process Friction' },
    { value: 'other', label: 'Other' }
  ];

  const severityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const sourceOptions = [
    { value: 'manual', label: 'Manual' },
    { value: 'scan', label: 'Scan' },
    { value: 'mission', label: 'Mission' },
    { value: 'objective', label: 'Objective' },
    { value: 'activity', label: 'Activity' },
    { value: 'task', label: 'Task' },
    { value: 'progress_report', label: 'Progress Report' }
  ];

  const reportedByOptions = officers.map(officer => ({
    value: officer.user_email,
    label: `${officer.rank_title} ${officer.callsign}`
  }));

  // Build context options from missions, objectives, activities
  const contextOptions = [
    ...missions.map(m => ({ value: `mission:${m.id}`, label: `[${m.mission_code}] ${m.title}` })),
    ...objectives.map(o => ({ value: `objective:${o.id}`, label: `[${o.objective_code}] ${o.statement?.substring(0, 40)}...` })),
    ...activities.map(a => ({ value: `activity:${a.id}`, label: `[${a.activity_code}] ${a.title}` }))
  ];

  const officerTagOptions = officers.map(o => ({
    value: o.user_email,
    label: `${o.rank_title} ${o.callsign}`
  }));

  const artifactTagOptions = artifacts.map(a => ({
    value: a.name,
    label: a.name
  }));

  const ritualTagOptions = rituals.map(r => ({
    value: r.name,
    label: r.name
  }));

  const policyTagOptions = policies.map(p => ({
    value: p.title,
    label: p.title
  }));

  const capabilityTagOptions = capabilities.map(c => ({
    value: c.name,
    label: c.name
  }));

  // Helper to get issue's context (mission, objective, activity IDs)
  const getIssueContextIds = (issue) => {
    const contextIds = [];
    
    let activityId = issue.linked_activity_id;
    let objectiveId = issue.linked_objective_id;
    let missionId = issue.linked_mission_id;

    // Derive from source_id if not directly linked
    if (!activityId && !objectiveId && !missionId && issue.source !== 'manual') {
      if (issue.source === 'activity' || issue.source === 'progress_report' || issue.source === 'task') {
        activityId = issue.source_id || issue.activity_id;
      } else if (issue.source === 'objective') {
        objectiveId = issue.source_id;
      } else if (issue.source === 'mission') {
        missionId = issue.source_id;
      }
    }

    // Derive upward
    if (activityId) {
      contextIds.push(`activity:${activityId}`);
      const activity = activities.find(a => a.id === activityId);
      if (activity?.objective_id) {
        objectiveId = activity.objective_id;
      }
    }
    if (objectiveId) {
      contextIds.push(`objective:${objectiveId}`);
      const objective = objectives.find(o => o.id === objectiveId);
      if (objective?.mission_id) {
        missionId = objective.mission_id;
      }
    }
    if (missionId) {
      contextIds.push(`mission:${missionId}`);
    }

    return contextIds;
  };

  useEffect(() => {
    let filtered = issues;

    // Search in title and description
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((issue) =>
        issue.title?.toLowerCase().includes(query) ||
        issue.description?.toLowerCase().includes(query)
      );
    }

    // Status filter (multi-select)
    if (statusFilter.length > 0) {
      filtered = filtered.filter((issue) => statusFilter.includes(issue.status));
    }

    // Category filter (multi-select)
    if (categoryFilter.length > 0) {
      filtered = filtered.filter((issue) => categoryFilter.includes(issue.category));
    }

    // Severity filter (multi-select)
    if (severityFilter.length > 0) {
      filtered = filtered.filter((issue) => severityFilter.includes(issue.severity));
    }

    // Source filter (multi-select)
    if (sourceFilter.length > 0) {
      filtered = filtered.filter((issue) => sourceFilter.includes(issue.source));
    }

    // Reported By filter (multi-select)
    if (reportedByFilter.length > 0) {
      filtered = filtered.filter((issue) => reportedByFilter.includes(issue.reported_by));
    }

    // Context filter (multi-select)
    if (contextFilter.length > 0) {
      filtered = filtered.filter((issue) => {
        const issueContextIds = getIssueContextIds(issue);
        return contextFilter.some(ctx => issueContextIds.includes(ctx));
      });
    }

    // Tagged Officers filter
    if (officerTagFilter.length > 0) {
      filtered = filtered.filter((issue) => {
        const tags = issue.tagged_officers || [];
        return officerTagFilter.some(tag => tags.includes(tag));
      });
    }

    // Tagged Artifacts filter
    if (artifactTagFilter.length > 0) {
      filtered = filtered.filter((issue) => {
        const tags = issue.tagged_artifacts || [];
        return artifactTagFilter.some(tag => tags.includes(tag));
      });
    }

    // Tagged Rituals filter
    if (ritualTagFilter.length > 0) {
      filtered = filtered.filter((issue) => {
        const tags = issue.tagged_rituals || [];
        return ritualTagFilter.some(tag => tags.includes(tag));
      });
    }

    // Tagged Policies filter
    if (policyTagFilter.length > 0) {
      filtered = filtered.filter((issue) => {
        const tags = issue.tagged_policies || [];
        return policyTagFilter.some(tag => tags.includes(tag));
      });
    }

    // Tagged Capabilities filter
    if (capabilityTagFilter.length > 0) {
      filtered = filtered.filter((issue) => {
        const tags = issue.tagged_capabilities || [];
        return capabilityTagFilter.some(tag => tags.includes(tag));
      });
    }

    onFilteredResults(filtered);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, categoryFilter, severityFilter, sourceFilter, reportedByFilter, contextFilter, officerTagFilter, artifactTagFilter, ritualTagFilter, policyTagFilter, capabilityTagFilter, issues]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter([]);
    setCategoryFilter([]);
    setSeverityFilter([]);
    setSourceFilter([]);
    setReportedByFilter([]);
    setContextFilter([]);
    setOfficerTagFilter([]);
    setArtifactTagFilter([]);
    setRitualTagFilter([]);
    setPolicyTagFilter([]);
    setCapabilityTagFilter([]);
  };

  const hasActiveFilters = searchQuery || statusFilter.length > 0 || categoryFilter.length > 0 || 
    severityFilter.length > 0 || sourceFilter.length > 0 || reportedByFilter.length > 0 ||
    contextFilter.length > 0 || officerTagFilter.length > 0 || artifactTagFilter.length > 0 ||
    ritualTagFilter.length > 0 || policyTagFilter.length > 0 || capabilityTagFilter.length > 0;

  const exportToCSV = () => {
    const headers = ['ID', 'Title', 'Description', 'Category', 'Severity', 'Status', 'Source', 'Reported By', 'Date'];
    
    const rows = issues.map(issue => {
      const reporter = officers.find(o => o.user_email === issue.reported_by);
      return [
        issue.id,
        `"${(issue.title || '').replace(/"/g, '""')}"`,
        `"${(issue.description || '').replace(/"/g, '""')}"`,
        issue.category || '',
        issue.severity || '',
        issue.status || '',
        issue.source || '',
        reporter ? reporter.callsign : issue.reported_by || '',
        issue.reported_at ? format(new Date(issue.reported_at), 'yyyy-MM-dd HH:mm') : 
          issue.created_date ? format(new Date(issue.created_date), 'yyyy-MM-dd HH:mm') : ''
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `issues_export_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  return (
    <Card className="glass-panel border-none mb-6">
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input data-testid="issues-issuesearch-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search issues by title or description..."
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="border-slate-600 text-slate-100 bg-slate-800 hover:bg-slate-700">
              {showAdvancedFilters ? <ChevronUp className="w-4 h-4 mr-2" /> : <ChevronDown className="w-4 h-4 mr-2" />}
              Filters
            </Button>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-slate-400 hover:text-slate-200">
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            )}
            
            {/* View Toggle */}
            {onViewModeChange && (
              <div className="flex border border-slate-700 rounded-md overflow-hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewModeChange('card')}
                  className={`rounded-none px-3 ${viewMode === 'card' ? 'bg-indigo-600 text-white' : 'text-slate-400'}`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewModeChange('table')}
                  className={`rounded-none px-3 ${viewMode === 'table' ? 'bg-indigo-600 text-white' : 'text-slate-400'}`}
                >
                  <Table2 className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {/* Export CSV */}
            <Button
              variant="outline"
              onClick={exportToCSV}
              className="border-slate-600 text-slate-100 bg-slate-800 hover:bg-slate-700"
              title="Export to CSV"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="space-y-4 pt-4 border-t border-slate-800">
              {/* Row 1: Basic filters */}
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <MultiSelectFilter
                  label="Status"
                  options={statusOptions}
                  selected={statusFilter}
                  onSelectionChange={setStatusFilter}
                  placeholder="All Statuses"
                />

                <MultiSelectFilter
                  label="Category"
                  options={categoryOptions}
                  selected={categoryFilter}
                  onSelectionChange={setCategoryFilter}
                  placeholder="All Categories"
                />

                <MultiSelectFilter
                  label="Severity"
                  options={severityOptions}
                  selected={severityFilter}
                  onSelectionChange={setSeverityFilter}
                  placeholder="All Severities"
                />

                <MultiSelectFilter
                  label="Source"
                  options={sourceOptions}
                  selected={sourceFilter}
                  onSelectionChange={setSourceFilter}
                  placeholder="All Sources"
                />

                <MultiSelectFilter
                  label="Reported By"
                  options={reportedByOptions}
                  selected={reportedByFilter}
                  onSelectionChange={setReportedByFilter}
                  placeholder="All Officers"
                />

                <MultiSelectFilter
                  label="Context"
                  options={contextOptions}
                  selected={contextFilter}
                  onSelectionChange={setContextFilter}
                  placeholder="All Contexts"
                />
              </div>

              {/* Row 2: Tagged element filters */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <MultiSelectFilter
                  label="Tagged Officers"
                  options={officerTagOptions}
                  selected={officerTagFilter}
                  onSelectionChange={setOfficerTagFilter}
                  placeholder="Any Officer"
                />

                <MultiSelectFilter
                  label="Tagged Artifacts"
                  options={artifactTagOptions}
                  selected={artifactTagFilter}
                  onSelectionChange={setArtifactTagFilter}
                  placeholder="Any Artifact"
                />

                <MultiSelectFilter
                  label="Tagged Rituals"
                  options={ritualTagOptions}
                  selected={ritualTagFilter}
                  onSelectionChange={setRitualTagFilter}
                  placeholder="Any Ritual"
                />

                <MultiSelectFilter
                  label="Tagged Policies"
                  options={policyTagOptions}
                  selected={policyTagFilter}
                  onSelectionChange={setPolicyTagFilter}
                  placeholder="Any Policy"
                />

                <MultiSelectFilter
                  label="Tagged Capabilities"
                  options={capabilityTagOptions}
                  selected={capabilityTagFilter}
                  onSelectionChange={setCapabilityTagFilter}
                  placeholder="Any Capability"
                />
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 pt-2">
              {searchQuery && (
                <Badge variant="outline" className="border-indigo-500/30 text-indigo-400">
                  Search: "{searchQuery}"
                </Badge>
              )}
              {statusFilter.map(s => (
                <Badge key={s} variant="outline" className="border-emerald-500/30 text-emerald-400">
                  Status: {statusOptions.find(o => o.value === s)?.label || s}
                </Badge>
              ))}
              {categoryFilter.map(c => (
                <Badge key={c} variant="outline" className="border-cyan-500/30 text-cyan-400">
                  Category: {categoryOptions.find(o => o.value === c)?.label || c}
                </Badge>
              ))}
              {severityFilter.map(s => (
                <Badge key={s} variant="outline" className="border-amber-500/30 text-amber-400">
                  Severity: {severityOptions.find(o => o.value === s)?.label || s}
                </Badge>
              ))}
              {sourceFilter.map(s => (
                <Badge key={s} variant="outline" className="border-purple-500/30 text-purple-400">
                  Source: {sourceOptions.find(o => o.value === s)?.label || s}
                </Badge>
              ))}
              {reportedByFilter.map(r => (
                <Badge key={r} variant="outline" className="border-pink-500/30 text-pink-400">
                  Reported By: {officers.find(o => o.user_email === r)?.callsign || r}
                </Badge>
              ))}
              {contextFilter.map(c => (
                <Badge key={c} variant="outline" className="border-blue-500/30 text-blue-400">
                  Context: {contextOptions.find(o => o.value === c)?.label || c}
                </Badge>
              ))}
              {officerTagFilter.map(o => (
                <Badge key={o} variant="outline" className="border-indigo-500/30 text-indigo-400">
                  Tagged Officer: {officers.find(off => off.user_email === o)?.callsign || o}
                </Badge>
              ))}
              {artifactTagFilter.map(a => (
                <Badge key={a} variant="outline" className="border-cyan-500/30 text-cyan-400">
                  Tagged Artifact: {a}
                </Badge>
              ))}
              {ritualTagFilter.map(r => (
                <Badge key={r} variant="outline" className="border-purple-500/30 text-purple-400">
                  Tagged Ritual: {r}
                </Badge>
              ))}
              {policyTagFilter.map(p => (
                <Badge key={p} variant="outline" className="border-amber-500/30 text-amber-400">
                  Tagged Policy: {p}
                </Badge>
              ))}
              {capabilityTagFilter.map(c => (
                <Badge key={c} variant="outline" className="border-emerald-500/30 text-emerald-400">
                  Tagged Capability: {c}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}